import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, onValue, query, orderByChild, equalTo, get } from 'firebase/database';
import './AdminIdentitySearch.css';

function AdminIdentitySearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [error, setError] = useState(null);
  const [activity, setActivity] = useState({
    reports: [],
    cases: []
  });

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    setIsSearching(true);
    setTargetUser(null);
    setError(null);
    setActivity({ reports: [], cases: [] });

    const cleanTerm = searchTerm.trim();

    try {
      const usersRef = ref(db, 'users');
      let foundUser = null;

      // PHASE 1: Direct Primary Key Hit (Most Efficient, No Index Required)
      try {
        const uidSnap = await get(ref(db, `users/${cleanTerm}`));
        if (uidSnap.exists()) {
          foundUser = uidSnap.val();
        }
      } catch (uidErr) {
        console.warn("Direct UID lookup bypassed or failed:", uidErr);
      }

      // PHASE 2: SUID Vector (If not found via UID)
      if (!foundUser) {
        try {
          const suidQuery = query(usersRef, orderByChild('suid'), equalTo(cleanTerm));
          const suidSnap = await get(suidQuery);
          if (suidSnap.exists()) {
            foundUser = Object.values(suidSnap.val())[0];
          }
        } catch (suidErr) {
          console.warn("SUID Index mission/failed:", suidErr);
        }
      }

      // PHASE 3: Username Vector
      if (!foundUser) {
        try {
          const nameQuery = query(usersRef, orderByChild('username'), equalTo(cleanTerm));
          const nameSnap = await get(nameQuery);
          if (nameSnap.exists()) {
            foundUser = Object.values(nameSnap.val())[0];
          }
        } catch (nameErr) {
          console.warn("Username Index missing/failed:", nameErr);
        }
      }

      // PHASE 4: Email Vector
      if (!foundUser && cleanTerm.includes('@')) {
        try {
          const emailQuery = query(usersRef, orderByChild('email'), equalTo(cleanTerm));
          const emailSnap = await get(emailQuery);
          if (emailSnap.exists()) {
            foundUser = Object.values(emailSnap.val())[0];
          }
        } catch (emailErr) {
          console.warn("Email Index missing/failed:", emailErr);
        }
      }

      if (foundUser) {
        setTargetUser(foundUser);
        fetchActivity(foundUser.uid || cleanTerm);
      } else {
        setError(`Identity "${cleanTerm}" not found. Ensure ID is copied exactly.`);
      }
    } catch (globalErr) {
      console.error(globalErr);
      setError("Intelligence Link Failure: Core sync error. Contact System Admin.");
    }
    setIsSearching(false);
  };

  const fetchActivity = async (uid) => {
    // Fetch Reports linked to this user
    const reportsRef = ref(db, 'reports');
    const reportsSnap = await get(reportsRef);
    if (reportsSnap.exists()) {
      const allReports = reportsSnap.val();
      const userReports = Object.values(allReports).filter(r => r.reportedUid === uid || r.reporterUid === uid);
      setActivity(prev => ({ ...prev, reports: userReports }));
    }

    // Fetch Cases linked to this user
    const casesRef = ref(db, 'cases');
    const casesSnap = await get(casesRef);
    if (casesSnap.exists()) {
      const allCases = casesSnap.val();
      const userCases = Object.values(allCases).filter(c => c.targetUid === uid);
      setActivity(prev => ({ ...prev, cases: userCases }));
    }
  };

  return (
    <div className="identity-search-portal animate-up">
      <div className="admin-page-header">
        <h1>Target Identity Intelligence</h1>
        <p>Cross-vector lookup engine for comprehensive platform visibility.</p>
      </div>

      <div className="search-dossier-container">
        <div className="search-interface admin-card">
          <form onSubmit={handleSearch} className="identity-search-bar">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input 
              type="text" 
              placeholder="Search by SUID, UID, Username, or Email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" disabled={isSearching}>
              {isSearching ? <i className="fa-solid fa-spinner fa-spin"></i> : "SCAN IDENTITY"}
            </button>
          </form>
          <div className="search-hint">
            <i className="fa-solid fa-circle-info"></i>
            <span>Tip: Exact match required for UID/SUID precision lookups.</span>
          </div>
        </div>

        {error && (
          <div className="search-error-box animate-down">
            <i className="fa-solid fa-triangle-exclamation"></i>
            {error}
          </div>
        )}

        {targetUser && (
          <div className="dossier-results animate-fade-in">
            <div className="dossier-grid">
              {/* PRIMARY IDENTITY */}
              <div className="admin-card dossier-card core-identity">
                <div className="card-header">
                  <i className="fa-solid fa-id-card-clip"></i>
                  <h3>Core Identity</h3>
                </div>
                <div className="dossier-content">
                  <div className="info-node">
                    <label>Full Name</label>
                    <strong>{targetUser.name}</strong>
                  </div>
                  <div className="info-node">
                    <label>System UID</label>
                    <code className="copy-node">{targetUser.uid}</code>
                  </div>
                  <div className="info-node">
                    <label>Origin SUID</label>
                    <code className="copy-node accent">{targetUser.suid || 'PENDING'}</code>
                  </div>
                  <div className="info-node">
                    <label>Email Access</label>
                    <strong>{targetUser.email}</strong>
                  </div>
                </div>
              </div>

              {/* GOVERNANCE & SAFETY */}
              <div className="admin-card dossier-card safety-standing">
                <div className="card-header">
                  <i className="fa-solid fa-shield-halved"></i>
                  <h3>Governance Standing</h3>
                </div>
                <div className="dossier-content">
                  <div className="status-badge-container">
                    <span className={`status-pill ${targetUser.status || 'active'}`}>
                      {targetUser.status?.toUpperCase() || 'ACTIVE'}
                    </span>
                  </div>
                  <div className="info-node">
                    <label>DNA Integrity Score</label>
                    <div className="dna-meter">
                      <div className="dna-fill" style={{width: `${targetUser.dnaScore || 85}%`}}></div>
                      <span>{targetUser.dnaScore || 85}/100</span>
                    </div>
                  </div>
                  <div className="info-node">
                    <label>Account Role</label>
                    <strong>{targetUser.role?.toUpperCase() || 'USER'}</strong>
                  </div>
                  <div className="info-node">
                    <label>Last Neural Login</label>
                    <strong>{targetUser.lastLogin ? new Date(targetUser.lastLogin).toLocaleString() : 'N/A'}</strong>
                  </div>
                </div>
              </div>

              {/* TECHNICAL FOOTPRINT */}
              <div className="admin-card dossier-card technical-footprint">
                <div className="card-header">
                  <i className="fa-solid fa-code-branch"></i>
                  <h3>Technical Footprint</h3>
                </div>
                <div className="dossier-content grid-2">
                  <div className="social-node">
                    <i className="fa-brands fa-github"></i>
                    <span>{targetUser.github || 'Not Linked'}</span>
                  </div>
                  <div className="social-node">
                    <i className="fa-solid fa-code"></i>
                    <span>{targetUser.leetcode || 'Not Linked'}</span>
                  </div>
                  <div className="social-node">
                    <i className="fa-solid fa-terminal"></i>
                    <span>{targetUser.codeforces || 'Not Linked'}</span>
                  </div>
                  <div className="social-node">
                    <i className="fa-brands fa-twitter"></i>
                    <span>{targetUser.twitter || 'Not Linked'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ACTIVITY TIMELINE (REPORTS & CASES) */}
            <div className="admin-card dossier-activity">
              <div className="card-header">
                <i className="fa-solid fa-clock-rotate-left"></i>
                <h3>Governance Activity History</h3>
              </div>
              <div className="activity-tabs">
                <div className="activity-section">
                  <h4>Associated Reports ({activity.reports.length})</h4>
                  {activity.reports.length > 0 ? (
                    <div className="activity-list">
                      {activity.reports.map((r, i) => (
                        <div key={i} className="activity-item report">
                          <span className="type">REPORT</span>
                          <span className="id">#{r.reportId?.slice(0, 8)}</span>
                          <span className="reason">{r.reason}</span>
                          <span className="status">{r.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="empty-text">No reports linked to this identity.</p>}
                </div>

                <div className="activity-section">
                  <h4>Managed Cases ({activity.cases.length})</h4>
                  {activity.cases.length > 0 ? (
                    <div className="activity-list">
                      {activity.cases.map((c, i) => (
                        <div key={i} className="activity-item case">
                          <span className="type">CASE</span>
                          <span className="id">#{c.caseId?.slice(0, 8)}</span>
                          <span className="subject">{c.subject}</span>
                          <span className="priority">{c.priority}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="empty-text">No active cases associated with this identity.</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminIdentitySearch;
